import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const Home = () => {
  const observerRef = useRef();

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('.animate-on-scroll');
    elements.forEach((el) => observerRef.current.observe(el));

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 via-white to-accent-50 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-10 left-10 w-20 h-20 bg-primary-500 rounded-full animate-float"></div>
          <div className="absolute top-1/3 right-20 w-16 h-16 bg-accent-500 rounded-full animate-float" style={{animationDelay: '1s'}}></div>
          <div className="absolute bottom-20 left-1/4 w-24 h-24 bg-primary-600 rounded-full animate-float" style={{animationDelay: '2s'}}></div>
        </div>

        <div className="container-max relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-7xl font-bold mb-6 animate-slide-up">
              <span className="gradient-text">Automation</span>
              <br />
              <span className="text-gray-900">Next Level</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-600 mb-8 animate-slide-up" style={{animationDelay: '0.2s'}}>
              Testing Tomorrow, Today
            </p>
            
            <p className="text-lg text-gray-700 mb-12 max-w-3xl mx-auto animate-slide-up" style={{animationDelay: '0.4s'}}>
              Revolutionary QA automation platform that reshapes how development teams approach testing. 
              We don't just test your applications—we accelerate your entire development lifecycle.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-slide-up" style={{animationDelay: '0.6s'}}>
              <a
                href="https://ejbed.com/app/"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary text-lg px-8 py-4 flex items-center space-x-2"
              >
                <span>Start Testing Now</span>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </a>
              <Link to="/services" className="btn-secondary text-lg px-8 py-4">
                Explore Services
              </Link>
            </div>

            <div className="mt-16 animate-slide-up" style={{animationDelay: '0.8s'}}>
              <p className="text-sm text-gray-500 mb-4">Trusted by development teams worldwide</p>
              <div className="flex justify-center items-center space-x-8 text-2xl font-bold text-primary-600">
                <span>Accelerate</span>
                <span className="text-gray-300">•</span>
                <span>Automate</span>
                <span className="text-gray-300">•</span>
                <span>Dominate</span>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </div>
      </section>

      {/* Current Services Section */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="text-center mb-16 animate-on-scroll">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              What We're Delivering <span className="gradient-text">Today</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our current automation platform is already transforming how teams test APIs and build reliable applications.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 items-center animate-on-scroll">
            <div>
              <img 
                src="https://images.unsplash.com/photo-1551650975-87deedd944c3?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80" 
                alt="API Testing Dashboard" 
                className="rounded-lg shadow-2xl"
              />
            </div>
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Intelligent API Testing</h3>
                  <p className="text-gray-600">
                    Our platform generates comprehensive REST Assured test suites automatically, eliminating manual test creation 
                    and reducing development time by up to 70%.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 bg-accent-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-accent-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Lightning-Fast Execution</h3>
                  <p className="text-gray-600">
                    Execute thousands of API tests in minutes, not hours. Our optimized engine processes complex test scenarios 
                    faster than traditional testing frameworks.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Zero Configuration Setup</h3>
                  <p className="text-gray-600">
                    Unlike complex testing tools that require weeks of setup, EJBED is ready to use in minutes. 
                    Import your API specifications and start testing immediately.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Future Roadmap Section */}
      <section className="section-padding bg-gray-50">
        <div className="container-max">
          <div className="text-center mb-16 animate-on-scroll">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              The Future is <span className="gradient-text">Coming Soon</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We're building the most comprehensive testing automation platform. Here's what's next on our roadmap.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 animate-on-scroll">
            {/* UI Testing */}
            <div className="bg-white rounded-xl p-8 shadow-lg card-hover">
              <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg flex items-center justify-center mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">UI Testing Automation</h3>
              <p className="text-gray-600 mb-6">
                Complete end-to-end UI testing with intelligent element detection, cross-browser compatibility, 
                and visual regression testing capabilities.
              </p>
              <div className="text-purple-600 font-semibold">Q4 2025</div>
            </div>

            {/* Performance Testing */}
            <div className="bg-white rounded-xl p-8 shadow-lg card-hover">
              <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg flex items-center justify-center mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Performance Testing</h3>
              <p className="text-gray-600 mb-6">
                Advanced load testing, stress testing, and performance monitoring with real-time analytics 
                and bottleneck identification across your entire application stack.
              </p>
              <div className="text-orange-600 font-semibold">Q1 2026</div>
            </div>

            {/* Security Testing */}
            <div className="bg-white rounded-xl p-8 shadow-lg card-hover">
              <div className="w-16 h-16 bg-gradient-to-r from-red-500 to-red-600 rounded-lg flex items-center justify-center mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5-6l-5 5-5-5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Security Testing</h3>
              <p className="text-gray-600 mb-6">
                Comprehensive penetration testing, vulnerability assessment, and security compliance validation 
                with automated threat detection and remediation suggestions.
              </p>
              <div className="text-red-600 font-semibold">Q2 2026</div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose EJBED Section */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="text-center mb-16 animate-on-scroll">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Why Teams Choose <span className="gradient-text">EJBED</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              While others promise automation, we deliver results. Here's what sets us apart from traditional testing tools.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 animate-on-scroll">
            <div className="text-center">
              <div className="text-4xl font-bold text-primary-600 mb-2">70%</div>
              <div className="text-gray-600">Faster Test Creation</div>
              <div className="text-sm text-gray-500 mt-2">vs. manual test writing</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-accent-600 mb-2">90%</div>
              <div className="text-gray-600">Reduced Setup Time</div>
              <div className="text-sm text-gray-500 mt-2">vs. traditional frameworks</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-primary-600 mb-2">50%</div>
              <div className="text-gray-600">Fewer Bugs in Production</div>
              <div className="text-sm text-gray-500 mt-2">with comprehensive testing</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-accent-600 mb-2">24/7</div>
              <div className="text-gray-600">Continuous Testing</div>
              <div className="text-sm text-gray-500 mt-2">automated CI/CD integration</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-r from-primary-600 to-primary-900 text-white">
        <div className="container-max text-center animate-on-scroll">
          <h2 className="text-4xl font-bold mb-6">Ready to Transform Your Testing?</h2>
          <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
            Join forward-thinking development teams who've already made the switch to next-level automation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href="https://ejbed.com/app/"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white text-primary-600 hover:bg-gray-100 font-semibold py-4 px-8 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
            >
              Start Free Trial
            </a>
            <Link 
              to="/contact" 
              className="border-2 border-white text-white hover:bg-white hover:text-primary-600 font-semibold py-4 px-8 rounded-lg transition-all duration-300 transform hover:scale-105"
            >
              Schedule Demo
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home; 